import { Group, WorkflowGroup } from "../types/types"
import { genericRequest, groupsRoute, workflowGroupRoute } from "./service"

/**
 * Gets the list of all groups.
 * 
 * @returns List of all groups stored in the database
 */
export const getGroups = async (): Promise<Group[] | Error> => {
  return await genericRequest({ url: `${groupsRoute}/root/groups`, method: "GET" })
}

/**
 * Gets a group for a given id.
 * 
 * @param groupId of the group to get
 * 
 * @returns group with given id
 */
export const getGroup = async (groupId: number): Promise<Group | Error> => {
  return await genericRequest({ url: `${groupsRoute}/${groupId}`, method: "GET" })
}

/**
 * Gets all groups with a given parent id.
 * 
 * @param parentId of the parent group
 * 
 * @returns groups with parent id
 */
export const getGroupsWithParent = async (parentId: number): Promise<Group[] | Error> => {
  return await genericRequest({ url: `${groupsRoute}/parent/${parentId}`, method: "GET" })
}

/**
 * Gets all workflow groups.
 * 
 * @returns list of all workflow groups
 */
export const getWorkflowGroups = async (limit?: number, offset?: number): Promise<WorkflowGroup[] | Error> => {
  const query = {
    limit: `${limit ?? 100}`,
    skip: `${offset ?? 0}`,
  };
  return await genericRequest({ url: `${workflowGroupRoute}/`, method: "GET", query: query })
}

/**
 * Adds a group to the database.
 * 
 * @param groupName name of the group to store
 * 
 * @returns created group
 */
export const addGroup = async (groupName: string, parentId?: number): Promise<Group | Error> => {
  const body = JSON.stringify({
    name: groupName
  })
  const url = parentId !== undefined ? `${groupsRoute}/${parentId}` : `${groupsRoute}/`

  return await genericRequest({ url: url, method: "POST", body: body })
}

export const deleteGroup = async (groupId: number): Promise<Group | Error> => {
  return await genericRequest({ url: `${groupsRoute}/${groupId}`, method: "DELETE" })
}

/**
 * Adds a workflow to a given group.
 * 
 * @param groupId of group to add workflow to
 * @param workflowId of workflow to add to the group
 * 
 * @returns workflow group
 */
export const addWorkflowGroup = async (groupId: number, workflowId: number): Promise<WorkflowGroup | Error> => {
  const body = JSON.stringify({
    workflow_id: workflowId,
    group_id: groupId
  })
  return await genericRequest({ url: `${workflowGroupRoute}/`, method: "POST", body: body })
}

/**
 * Updates a given group.
 * 
 * @param groupId group id to update
 * @param group updated group object
 * 
 * @returns group object with updated values
 */
export const updateGroup = async (groupId: number, group: Group): Promise<Group | Error> => {
  const body = JSON.stringify(group)
  return await genericRequest({ url: `${groupsRoute}/${groupId}`, method: "PUT", body: body })
}

/**
 * Deletes a workflow from a group.
 * 
 * @param workflowGroupId to delete
 * 
 * @returns null for successful deletion
 */
export const removeWorkflowGroup = async (groupId: number, workflowId: number): Promise<Group | Error> => {
  return await genericRequest({ url: `${workflowGroupRoute}/${workflowId}/${groupId}`, method: "DELETE" })
}